import React from 'react';

type Props = {
  /** Text to split. Use "\n" for a line break. */
  text: string;
  /** Substring rendered with the gradient `pop-text` style. */
  highlight?: string;
  className?: string;
};

/**
 * Splits text into words wrapped in clipping spans so each word can slide up
 * with a stagger. Reveal is driven by the nearest `.reveal.is-visible` ancestor
 * (see global.css), so drop it inside any revealed heading.
 */
const SplitText: React.FC<Props> = ({ text, highlight, className }) => {
  const lines = text.split('\n');
  let index = 0;

  const renderWord = (word: string, key: string, pop: boolean) => {
    const style = { '--i': index++ } as React.CSSProperties;
    return (
      <React.Fragment key={key}>
        {index > 1 && ' '}
        <span className="split__word" style={style}>
          <span className={`split__inner${pop ? ' pop-text' : ''}`}>{word}</span>
        </span>
      </React.Fragment>
    );
  };

  return (
    <span className={`split${className ? ` ${className}` : ''}`}>
      {lines.map((line, li) => {
        const parts: React.ReactNode[] = [];
        if (highlight && line.includes(highlight)) {
          const [before, after] = line.split(highlight);
          before.trim().split(/\s+/).filter(Boolean).forEach((w, i) => parts.push(renderWord(w, `${li}-b${i}`, false)));
          highlight.split(/\s+/).forEach((w, i) => parts.push(renderWord(w, `${li}-h${i}`, true)));
          after.trim().split(/\s+/).filter(Boolean).forEach((w, i) => parts.push(renderWord(w, `${li}-a${i}`, false)));
        } else {
          line.trim().split(/\s+/).filter(Boolean).forEach((w, i) => parts.push(renderWord(w, `${li}-w${i}`, false)));
        }
        return (
          <React.Fragment key={li}>
            {parts}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </span>
  );
};

export default SplitText;
